import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import useUIStore from "@/stores/useUIStore";
import { IAccount } from "@/types";
import { tasks_v1 } from "@googleapis/tasks";
import { PlusIcon, UnplugIcon, GripVerticalIcon, CheckIcon, XIcon } from "lucide-react";
import { useInsertTasklist } from "@/hooks";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { useState, useMemo } from "react";
import BarLoader from "react-spinners/BarLoader";
import { useTheme } from "@/ThemeProvider";
import { getAccountDisplayName } from "@/helpers";
import RenameAccountDialog from "./RenameAccountDialog";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useConfigStore } from "@/stores/useConfigStore";

interface SiderbarItemProps {
  title: string,
  id: string,
  user: IAccount,
}

import "./AccountSiderList.css";
import DisconnectAccountDialog from "./DisconnectAccountDialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

const SiderbarItem = ({ title, id, user }: SiderbarItemProps) => {
  const checkboxes = useUIStore((state) => state.checkboxes);
  const setCheckbox = useUIStore((state) => state.setCheckbox);

  // Derive the value with a default - no state update during render
  const isChecked = checkboxes?.[user.id]?.[id] ?? true;

  return (
    <li key={id} className="flex items-center mb-1 px-3 rounded-xl cursor-pointer transition-colors hover:bg-card hover:shadow active:bg-card active:shadow gap-4 sidebar-item max-md:py-2">
      <Checkbox
        checked={isChecked}
        onCheckedChange={(c) => {
          setCheckbox(user.id, id, c as boolean)
        }}
        id={id}
        className="border-slate-300 dark:border-slate-600 rounded-[5px] transition-all duration-150 hover:border-blue-300 dark:hover:border-blue-400/60 hover:bg-blue-50/50 dark:hover:bg-blue-500/5 focus-visible:ring-blue-400/20 data-[state=checked]:bg-blue-100 data-[state=checked]:border-transparent data-[state=checked]:text-blue-700 dark:data-[state=checked]:bg-blue-500/15 dark:data-[state=checked]:border-transparent dark:data-[state=checked]:text-blue-300 max-md:h-5 max-md:w-5"
      />
      <label className="cursor-pointer w-full ph-no-capture" htmlFor={id}>{title}</label>
      <GripVerticalIcon className="h-6 w-6 text-muted-foreground p-0 m-0 opacity-25 transition-opacity grip-icon" />
    </li>
  );
};

interface AccountSiderListProps {
  user: IAccount,
  lists: (tasks_v1.Schema$TaskList & {accountId: string })[],
  listTitleMap: Record<string, string>
}

const AccountSiderList = ({ user, lists, listTitleMap }: AccountSiderListProps) => {
  const setCheckbox = useUIStore((state) => state.setCheckbox);
  const { theme } = useTheme();
  const accountNickname = useConfigStore((state) => state.config.accounts[user.id]?.nickname);

  const insertTasklistMutation = useInsertTasklist(user)
  const [open, setOpen] = useState(false);
  const [openRename, setOpenRename] = useState(false);
  const [openDisconnect, setOpenDisconnect] = useState(false);
  const [title, setTitle] = useState("");
  const [checkAll, setCheckAll] = useState(true);
  // const queryClient = useQueryClient();

  const storedOrder = useUIStore((state) => state.listOrder[user.id] || []);
  const setListOrder = useUIStore((state) => state.setListOrder);

  // Derive the display order at render time - no syncing needed
  const displayOrder = useMemo(() => {
    const allListIds = lists.map(list => list.id).filter((id): id is string => !!id);
    const validIds = new Set(allListIds);
    
    // Keep stored order items that still exist
    const validStoredOrder = storedOrder.filter(id => validIds.has(id));
    // Append any new items not in stored order
    const newIds = allListIds.filter(id => !storedOrder.includes(id));
    
    return [...validStoredOrder, ...newIds];
  }, [lists, storedOrder]);

  // const reconnect = async () => {
  //   await Promise.all([
  //     queryClient.invalidateQueries({ queryKey: ["accounts"] }),
  //     queryClient.invalidateQueries({ queryKey: ["tasklists"] }),
  //     queryClient.invalidateQueries({ queryKey: ["tasklist"] })
  //   ]);
  // }

  const onCheckAll = () => {
    lists.forEach(list => {
      if (list.id) setCheckbox(user.id, list.id, !checkAll);
    });
    setCheckAll(!checkAll);
  }

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    const newOrder = [...displayOrder];
    const [movedItem] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, movedItem);

    setListOrder(user.id, newOrder);
  };

  const isPrimaryAccount = user.primary === true;

  return (
    <>
      <div className="flex items-center gap-4 pl-2 pt-4 account-name-sider-container mb-1">
        <Avatar className="h-6 w-6 mb-1 ml-2 ph-no-capture">
          <AvatarImage src={user.picture} />
          <AvatarFallback>{`${user?.name?.charAt(0)}`}</AvatarFallback>
        </Avatar>

        <Tooltip>
          <TooltipTrigger asChild>
            <span className="font-semibold flex-1 cursor-pointer ph-no-capture" onClick={() => setOpenRename(true)}>{getAccountDisplayName(user, accountNickname, 18)}</span>
          </TooltipTrigger>
          <TooltipContent>
            Rename Account
          </TooltipContent>
        </Tooltip>
        {/* {mainUser?.id !== user.id && <Button size={"icon"} variant={"ghost"} onClick={reconnect}>
          <UpdateIcon className="h-4 w-4" />
        </Button>} */}
        <div className="flex mr-3">
          {/* <Button size={"icon"} variant={"ghost"} className="account-button rounded-full hover:bg-card hover:shadow" onClick={() => setOpenRename(true)}>
          <PencilIcon className="h-4 w-4 text-foreground" />
        </Button> */}
        {!isPrimaryAccount && 
        <Tooltip>
          <TooltipTrigger>
            <Button size={"icon"} variant={"ghost"} className="account-button rounded-full hover:bg-card hover:shadow" onClick={() => setOpenDisconnect(true)}>
              <UnplugIcon className="h-4 w-4 text-foreground" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            Disconnect Account
          </TooltipContent>
        </Tooltip>}

        <Tooltip>
          <TooltipTrigger>
            <Button size={"icon"} variant={"ghost"} className="account-button rounded-full hover:bg-card hover:shadow" onClick={onCheckAll}>
              {checkAll ? <XIcon strokeWidth={2.5} className="h-4 w-4 text-foreground" /> : <CheckIcon strokeWidth={2.5} className="h-4 w-4 text-foreground" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {checkAll ? "Uncheck All" : "Check All"}
          </TooltipContent>
        </Tooltip>
        </div>
      </div>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={`list-${user.id}`}>
          {(provided) => (
            <ul 
              className="mb-4 pl-2 pr-4"
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {displayOrder.map(
                (id, index) => {
                  if (!id) return null;
                  const list = lists.find(l => l.id === id);
                  if (!list || !listTitleMap[id]) return null;
                  
                  return (
                    <Draggable key={id} draggableId={id} index={index}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <SiderbarItem
                            key={id}
                            id={id}
                            title={listTitleMap[id]}
                            user={user}
                          />
                        </div>
                      )}
                    </Draggable>
                  );
                }
              )}
              {provided.placeholder}
              {/* {!insertTasklistMutation.isPending && <div className="w-full flex p-4"><PuffLoader size="1rem" loading={true} /></div>} */}
              {insertTasklistMutation.isPending && <div className="w-full flex justify-center p-4"><BarLoader width="100%" color={theme === "dark" ? "white" : "black"} loading={true} /></div>}
              <li
                // onClick={() => {
                //   console.log("creating list");
                //   insertTasklistMutation.mutate({ title: "New List", account: user });
                // }}
                className="flex items-center py-1 px-3 rounded-xl cursor-pointer transition-colors hover:bg-card hover:shadow gap-4 max-md:py-2.5"
              >
                <Dialog open={open} onOpenChange={setOpen}>
                  <DialogTrigger className="flex gap-4 items-center w-full">
                    <PlusIcon className="h-4 w-4" />
                    Create List
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>List Name</DialogTitle>
                    </DialogHeader>
                    <form
                      onSubmit={async (event) => {
                        event.preventDefault();
                        insertTasklistMutation.mutate({ title, account: user });
                        setOpen(false);
                        setTitle("");
                      }}
                    >
                      <Input required={true} className="mb-4" onChange={(e) => setTitle(e.target.value)} />
                      <div className="flex justify-end w-full gap-2">
                        <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">
                          OK
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </li>
            </ul>
          )}
        </Droppable>
      </DragDropContext>
      <RenameAccountDialog open={openRename} onOpenChange={setOpenRename} account={user} />
      <DisconnectAccountDialog open={openDisconnect} onOpenChange={setOpenDisconnect} account={user} />
    </>
  );
};

export default AccountSiderList;
